import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import clsx from 'clsx';
import type { EmailTemplate } from '../../types';

/**
 * The compose form's template picker.
 *
 * A native `<select>` only has prefix typeahead, so a template called "Rock — exampleband" could
 * not be reached by typing the band's name. This is a combobox instead: the list is filtered on a
 * case-insensitive substring of the name, arrows move the highlight and Enter picks it.
 *
 * The list is portalled to the body with fixed coordinates, like the schedule picker next to it,
 * because the compose column is an `overflow-y-auto` panel that would otherwise clip it.
 */

/**
 * The template picked most recently, pinned to the top of the unfiltered list so writing to a run
 * of contacts with the same template is one click each. Module state on purpose: it is shared by
 * every mount of the picker — the overlay remounts as contacts change — and nothing persists it,
 * so it is forgotten on reload.
 */
let lastPickedTemplateId: string | null = null;

/** Gap between the trigger and the list, and the margin the list keeps from the viewport edges. */
const GAP = 4;
const MARGIN = 8;
const MAX_LIST_HEIGHT = 264;
/** Above the modal backdrop's 9999, since this lives inside the contact overlay. */
const Z_INDEX = 10050;

interface TemplateSelectProps {
  templates: EmailTemplate[];
  /** Selected template id, or '' for none. */
  value: string;
  onChange: (templateId: string) => void;
  placeholder?: string;
  className?: string;
}

interface Placement {
  left: number;
  top: number;
  width: number;
  /** Set when the list is laid out upwards from the trigger. */
  flipped: boolean;
}

export function TemplateSelect({
  templates,
  value,
  onChange,
  placeholder = 'Select a template...',
  className,
}: TemplateSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  const sorted = useMemo(
    () => [...templates].sort((a, b) => a.name.localeCompare(b.name)),
    [templates]
  );

  // Mirrors the module value into state so picking one re-renders the list under the next open.
  const [lastPicked, setLastPicked] = useState(lastPickedTemplateId);

  const ordered = useMemo(() => {
    const last = lastPicked ? sorted.find((t) => t._id === lastPicked) : undefined;
    if (!last) return sorted;
    return [last, ...sorted.filter((t) => t._id !== last._id)];
  }, [sorted, lastPicked]);

  // A filter query goes back to plain alphabetical order: once a name has been typed, the match
  // belongs where the name puts it, not above the template the last mail happened to use.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return ordered;
    return sorted.filter((t) => t.name.toLowerCase().includes(query));
  }, [sorted, ordered, search]);

  /** True while the first row of the list is the pinned template rather than a plain `A`-first. */
  const pinnedFirst = !search.trim() && !!lastPicked && filtered[0]?._id === lastPicked;

  const selectedName = templates.find((t) => t._id === value)?.name ?? '';

  // Measured off the trigger, so the panel's first paint is already the right width in the right
  // place; the layout effect below then refines it against the list's real height.
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const height = Math.min(MAX_LIST_HEIGHT, listRef.current?.offsetHeight ?? MAX_LIST_HEIGHT);
    const below = rect.bottom + GAP + height < window.innerHeight - MARGIN;
    setPlacement({
      left: rect.left,
      top: below ? rect.bottom + GAP : Math.max(rect.top - GAP - height, MARGIN),
      width: rect.width,
      flipped: !below,
    });
  }, []);

  const open = (initialSearch = '') => {
    setSearch(initialSearch);
    setActiveIndex(initialSearch.trim() ? 1 : 0);
    place();
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setSearch('');
    setPlacement(null);
  };

  const pick = (templateId: string) => {
    // "No template" is not a template: it clears the selection and leaves the pinned row alone.
    if (templateId) {
      lastPickedTemplateId = templateId;
      setLastPicked(templateId);
    }
    onChange(templateId);
    close();
    triggerRef.current?.focus();
  };

  // Re-place the list whenever it is resized by the filter, or the page moves under it.
  useLayoutEffect(() => {
    if (!isOpen) return;
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isOpen, filtered.length, place]);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  // Keep the highlight visible when the arrows walk past the list's edge.
  useLayoutEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen, placement]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || listRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Typing on the closed trigger opens the list on that character, so the overlay's single-letter
  // shortcuts ("f", "p", "t") can never swallow the start of a search.
  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      open();
    } else if (e.key.length === 1 && e.key !== ' ') {
      e.preventDefault();
      e.stopPropagation();
      open(e.key);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Options include the "No template" row at index 0.
    const count = filtered.length + 1;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % count);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + count) % count);
        break;
      case 'Enter': {
        e.preventDefault();
        const index = Math.min(activeIndex, count - 1);
        pick(index === 0 ? '' : filtered[index - 1]._id);
        break;
      }
      case 'Escape':
        e.preventDefault();
        // The overlay closes on Escape from a `document` listener.
        e.stopPropagation();
        close();
        triggerRef.current?.focus();
        break;
    }
  };

  const optionClass = (active: boolean, selected: boolean) =>
    clsx(
      'w-full text-left px-3 py-2 text-sm truncate',
      active && 'bg-primary-600/20',
      selected ? 'text-primary-400' : 'text-dark-text-secondary',
      !active && 'hover:bg-dark-elevated'
    );

  return (
    <div className={clsx('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleTriggerKeyDown}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-sm text-dark-text-primary hover:bg-dark-border focus:outline-hidden focus:ring-2 focus:ring-primary-500"
      >
        <span className={clsx('truncate', !selectedName && 'text-dark-text-muted')}>
          {selectedName || placeholder}
        </span>
        <ChevronDown
          className={clsx(
            'w-4 h-4 flex-shrink-0 text-dark-text-secondary transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={listRef}
            className="fixed flex flex-col bg-dark-surface border border-dark-border rounded-lg shadow-xl overflow-hidden"
            style={{
              left: placement?.left ?? 0,
              top: placement?.top ?? 0,
              width: placement?.width,
              zIndex: Z_INDEX,
              visibility: placement ? 'visible' : 'hidden',
            }}
          >
            <div
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 border-dark-border',
                placement?.flipped ? 'order-last border-t' : 'border-b'
              )}
            >
              <Search className="w-3.5 h-3.5 flex-shrink-0 text-dark-text-muted" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setActiveIndex(e.target.value.trim() ? 1 : 0);
                }}
                onKeyDown={handleSearchKeyDown}
                placeholder="Filter templates..."
                className="w-full bg-transparent text-sm text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden"
              />
            </div>
            <div className="max-h-56 overflow-y-auto" role="listbox">
              <button
                ref={activeIndex === 0 ? activeRef : null}
                type="button"
                role="option"
                aria-selected={!value}
                onMouseEnter={() => setActiveIndex(0)}
                onClick={() => pick('')}
                className={clsx(optionClass(activeIndex === 0, !value), 'italic')}
              >
                No template
              </button>
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-sm text-dark-text-muted text-center">
                  No matching template
                </div>
              ) : (
                filtered.map((template, i) => (
                  <button
                    key={template._id}
                    ref={activeIndex === i + 1 ? activeRef : null}
                    type="button"
                    role="option"
                    aria-selected={template._id === value}
                    onMouseEnter={() => setActiveIndex(i + 1)}
                    onClick={() => pick(template._id)}
                    className={clsx(
                      optionClass(activeIndex === i + 1, template._id === value),
                      // Keeps the one out-of-order row from reading as a broken sort.
                      i === 0 && pinnedFirst && 'border-b border-dark-border'
                    )}
                    title={template.name}
                  >
                    {template.name}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
